import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery, gql } from "@apollo/client";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { StorageHelper } from "../utils/storageHelper";

/**
 * RBAC Context and Provider for React application.
 * - Fetches and caches user permissions from backend (via Apollo GraphQL).
 * - Provides hooks and helpers for checking module/action/project access.
 * - Handles permission caching, invalidation, and super admin bypass.
 * - Exposes ProtectedRoute for route-level RBAC enforcement.
 */

const GET_RBAC = gql`
  query GetRbac {
    GetRbac
  }
`;

const RbacContext = createContext(null);

export const RbacProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const { token } = useAuth();

  // Load cached permissions on mount
  useEffect(() => {
    const cachedPermissions = StorageHelper.get("permissions");
    if (cachedPermissions) {
      setPermissions(cachedPermissions);
    }
  }, []);

  // Fetch permissions from backend
  const { data, loading, error } = useQuery(GET_RBAC, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    skip: !StorageHelper.get("token"),
  });

  // Update permissions when new data arrives
  useEffect(() => {
    if (data?.GetRbac) {
      setPermissions(data.GetRbac);
      StorageHelper.set("permissions", data.GetRbac);
    }
  }, [data]);

  // Invalidate permissions if token is removed/logged out
  useEffect(() => {
    if (!StorageHelper.get("token")) {
      setPermissions(null);
      StorageHelper.remove("permissions");
    }
  }, [token]);

  const value = {
    permissions,
    loading: loading && !permissions,
    error,
  };

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
};

/**
 * Custom hook to access RBAC context and permission helpers.
 * - Throws if used outside RbacProvider.
 * - Provides helpers for module/action/project access and route protection.
 */
export const useRbac = () => {
  const context = useContext(RbacContext);
  if (!context) {
    throw new Error("useRbac must be used within a RbacProvider");
  }

  const { permissions, loading, error } = context;
  const { isLoggedIn, token } = useAuth();

  /**
   * Checks if user has access to a module (global or project).
   * @param {string} module - Module name.
   * @returns {boolean}
   */
  const hasModuleAccess = (module) => {
    if (!module || !permissions) {
      return false;
    }

    if (module === "dashboard") {
      return true;
    }

    if (permissions?.globalRole?.permissions?.access) {
      const modulePermissions = permissions.globalRole.permissions.access.find(
        (p) => p.module.toLowerCase() === module.toLowerCase()
      );
      if (modulePermissions && modulePermissions.value === true) {
        return true;
      }
    }

    return false;
  };

  /**
   * Checks if user has permission for a specific action in a module.
   * @param {string} module - Module name.
   * @param {string} action - Action key.
   * @returns {boolean}
   */
  const hasActionPermission = (module, action) => {
    if (!module || !action || !permissions) {
      return false;
    }
    if (permissions?.globalRole?.permissions?.access) {
      const modulePermissions = permissions.globalRole.permissions.access.find(
        (p) => p.module.toLowerCase() === module.toLowerCase()
      );

      if (modulePermissions?.value === true) {
        const actionPermission = modulePermissions.children?.find(
          (c) => c.key.toLowerCase() === action.toLowerCase()
        );

        if (actionPermission?.value === true) {
          return true;
        }
      }
    }
    if (permissions?.projectRoles && permissions.projectRoles.length > 0) {
      const hasProjectActionAccess = permissions.projectRoles.some(
        (projectRole) => {
          if (!projectRole?.role?.permissions?.access) return false;

          const modulePermissions = projectRole.role.permissions.access.find(
            (p) => p.module.toLowerCase() === module.toLowerCase()
          );

          if (modulePermissions?.value !== true) return false;

          const actionPermission = modulePermissions.children?.find(
            (c) => c.key.toLowerCase() === action.toLowerCase()
          );

          return actionPermission?.value === true;
        }
      );

      if (hasProjectActionAccess) {
        return true;
      }
    }

    return false;
  };

  /**
   * ProtectedRoute: React component for RBAC-protected routes.
   * - Checks login, permissions, super admin, and module/action/project access.
   * - Redirects to login or unauthorized page if access denied.
   */
  const ProtectedRoute = ({ children, module, action, projectId }) => {
    if (!token || !isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (loading && !permissions) {
      return <div>Loading permissions...</div>;
    }

    if (!permissions) {
      return <Navigate to="/unauthorized" replace />;
    }

    // Super admin bypass (from env)
    const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
      ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
          parseInt(id.trim())
        )
      : [];

    const isSuperAdmin = superAdminRoleIds.includes(
      parseInt(permissions?.globalRole?.role_id)
    );

    // if (isSuperAdmin) {
    //   return children;
    // }

    // Project-level access without module
    if (projectId && !module) {
      if(isSuperAdmin){
        return children;
      }
      const projectRole = permissions.projectRoles?.find(
        (pr) => Number(pr.project_id) === Number(projectId) && pr.role !== null
      );

      if (!projectRole || ![3, 8, 9, 1].includes(projectRole.role?.role_id) || permissions?.globalRole?.role_id === 8) {
        return <Navigate to="/unauthorized" replace />;
      }
      return children;
    }

    // Project-level module/action access
    if (projectId && module) {
      const projectRole = permissions.projectRoles?.find(
        (pr) => pr.project_id === projectId && pr.role !== null
      );

      if (!projectRole) {
        return <Navigate to="/unauthorized" replace />;
      }

      const modulePermissions = projectRole.role.permissions.access.find(
        (p) => p.module.toLowerCase() === module.toLowerCase()
      );

      if (!modulePermissions?.value) {
        return <Navigate to="/unauthorized" replace />;
      }

      if (action) {
        const actionPermission = modulePermissions.children?.find(
          (c) => c.key === action
        );

        if (!actionPermission?.value) {
          return <Navigate to="/unauthorized" replace />;
        }
      }

      return children;
    }

    // Global module/action access
    const moduleAccess = hasModuleAccess(module);
    if (!moduleAccess) {
      return <Navigate to="/unauthorized" replace />;
    }

    if (action) {
      const actionAccess = hasActionPermission(module, action);
      if (!actionAccess) {
        return <Navigate to="/unauthorized" replace />;
      }
    }

    return children;
  };

  /**
   * Checks access for a module/action, optionally for a project.
   * @param {string} module - Module name.
   * @param {string} action - Action key.
   * @param {number|null} projectId - Project ID.
   * @returns {boolean}
   */
  const checkAccess = (module, action, projectId = null) => {
    if (!module) return false;
    if (!permissions) return false;
    if (projectId) {
      const projectRole = permissions.projectRoles?.find(
        (pr) => pr.project_id === projectId
      );

      if (projectRole?.role?.permissions?.access) {
        const modulePermissions = projectRole.role.permissions.access.find(
          (p) => p.module === module
        );

        if (!modulePermissions?.value) return false;

        if (!action) return true;

        const actionPermission = modulePermissions.children?.find(
          (c) => c.key === action
        );
        return actionPermission?.value || false;
      }
    }
    const modulePermissions = permissions.globalRole?.permissions?.access?.find(
      (p) => p.module === module
    );

    if (!modulePermissions?.value) return false;

    if (!action) return true;

    const actionPermission = modulePermissions.children?.find(
      (c) => c.key === action
    );
    return actionPermission?.value || false;
  };

  /**
   * Checks if user has project-level permission for a module/action.
   * @param {number} projectId - Project ID.
   * @param {string} module - Module name.
   * @param {string} action - Action key.
   * @returns {boolean}
   */
  const hasProjectPermission = (projectId, module, action) => {
    if (!permissions?.projectRoles) return false;
    if (projectId && !module) {
      const projectRole = permissions.projectRoles.find(
        (pr) => pr.project_id === projectId && pr.role !== null
      );
      return !!projectRole;
    }

    const projectRole = permissions.projectRoles.find(
      (pr) => pr.project_id === projectId
    );

    if (!projectRole?.role?.permissions?.access) return false;

    const modulePermissions = projectRole.role.permissions.access.find(
      (p) => p.module === module
    );

    if (!modulePermissions || !modulePermissions.value) return false;

    if (!action) return modulePermissions.value;

    const actionPermission = modulePermissions.children.find(
      (c) => c.key === action
    );

    return actionPermission?.value || false;
  };

  return {
    loading,
    error,
    checkAccess,
    hasModuleAccess,
    hasActionPermission,
    hasProjectPermission,
    permissions,
    ProtectedRoute,
  };
};
